import React, { PropsWithChildren } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { store } from './app/store';
import reportWebVitals from './reportWebVitals';
import './index.scss';
import App from './app/default/App';
import AppOnPremise from './app/onpremise/AppOnPremise';
import { getIdentityProvider } from './authentication/authentication';
import { AuthHookEnum } from './authentication/utils';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, RouterProvider, useNavigate, Navigate } from 'react-router-dom';
import { AssetView } from './features/asset-view/AssetView';
import { StartPage } from './features/start-page/StartPage';
import { Settings } from './features/settings/Settings';
import { AppState, Auth0ContextInterface, Auth0Provider, Auth0ProviderOptions, User } from '@auth0/auth0-react';
import { ApiProvider } from './api';
import { ErrorBoundary } from './components/error-boundary/ErrorBoundary';
import { ErrorPageNotFound } from './components/error-boundary/ErrorPage';
import { Upload } from './features/data-upload/Upload';
import TaskExecution from './features/task-execution/TaskExecution';
import { UserProvider } from './app/hooks';
import ChartTypesOverview from './features/visual-analytics/chart-types-documentation/ChartTypesDocumentationOverview';
import AdminPortal from './features/admin/pages/AdminPortal';
import OrganizationSettings from './features/admin/pages/OrganizationSettings';
import { M2MApiProvider } from './api/m2mApi';
import { AuthorizationGuard, PERMISSIONS } from './components/authorization-guard/AuthorizationGuard';
import { AuthProvider, AuthProviderProps } from 'react-oidc-context';
import { slugify } from './features/visual-analytics/hooks/useExportFilename';

// Make sure VITE_SENTRY_DSN is set, e.g., via .env files copied during build or other means
const dsn = import.meta.env.VITE_SENTRY_DSN || '';
const releaseVersion = import.meta.env.VITE_SENTRY_RELEASE;
const appEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT!;

if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  Sentry.init({
    enabled: false,
  });
} else {
  Sentry.init({
    dsn: dsn,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE!, //  Capture 100% of the transactions
    // Session Replay
    environment: appEnvironment,
    release: releaseVersion,
    replaysSessionSampleRate: import.meta.env.VITE_SENTRY_PROFILES_SAMPLE_RATE!, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const modularCreateBrowserRouter = (AuthComponent: React.FC<object>, AppComponent: React.FC<object>) => {
  return createBrowserRouter([
    {
      path: '/',
      element: (
        <AuthComponent>
          <ErrorBoundary>
            <AppComponent />
          </ErrorBoundary>
        </AuthComponent>
      ),
      errorElement: (
        <AuthComponent>
          <ErrorPageNotFound />
        </AuthComponent>
      ),
      children: [
        {
          path: '',
          element: <StartPage />,
        },
        {
          path: 'asset/:assetId',
          element: <AssetView />,
        },
        {
          path: 'settings',
          element: (
            <AuthorizationGuard permissions={[PERMISSIONS.ADMIN, PERMISSIONS.WRITE]}>
              <Settings />
            </AuthorizationGuard>
          ),
        },
        {
          path: 'upload/:uploadTypeId',
          element: <Upload />,
        },
        {
          path: 'task-executions',
          element: <TaskExecution />,
        },
        {
          path: 'charts-documentation',
          element: (
            <AuthorizationGuard permissions={[PERMISSIONS.ADMIN, PERMISSIONS.WRITE]}>
              <ChartTypesOverview />
            </AuthorizationGuard>
          ),
        },
        {
          path: 'admin',
          element: (
            <AuthorizationGuard permissions={[PERMISSIONS.SYSTEM_ADMIN]}>
              <AdminPortal />
            </AuthorizationGuard>
          ),
        },
        {
          path: 'organization',
          element: (
            <AuthorizationGuard permissions={[PERMISSIONS.ADMIN]}>
              <OrganizationSettings />
            </AuthorizationGuard>
          ),
        },
      ],
    },
    {
      // Ultimate fallback route if user passes incorrect URI
      path: '*',
      element: <Navigate to="/" replace />,
    },
  ]);
};

const router = () => {
  const idprovider = getIdentityProvider();

  switch (idprovider) {
    case AuthHookEnum.Auth0: {
      /**
       * Auth0Provider component with custom Redirect callback which will
       * navigate to the path specified in the URL. By Default,
       * the Auth0Provider will always redirect to the root.
       *
       * See https://github.com/auth0/auth0-react examples
       */
      const Auth0ProviderWithRedirectCallback = ({
        children,
        context,
        ...props
      }: PropsWithChildren<Omit<Auth0ProviderOptions, 'context'>> & {
        context?: React.Context<Auth0ContextInterface<User>>;
      }) => {
        const navigate = useNavigate();

        const onRedirectCallback = (appState?: AppState, user?: User) => {
          navigate(appState?.returnTo || window.location.pathname);
        };

        return (
          <Auth0Provider onRedirectCallback={onRedirectCallback} context={context} {...props}>
            {children}
          </Auth0Provider>
        );
      };

      const Auth0ComponentProvider = ({ children, ...props }: PropsWithChildren) => {
        return (
          <Auth0ProviderWithRedirectCallback
            domain={import.meta.env.VITE_AUTH0_DOMAIN!}
            clientId={import.meta.env.VITE_AUTH0_CLIENT_ID!}
            authorizationParams={{
              redirect_uri: window.location.origin,
              audience: import.meta.env.VITE_AUTH0_AUDIENCE!,
            }}
          >
            <UserProvider>
              <Provider store={store}>
                <ApiProvider>
                  <M2MApiProvider>{children}</M2MApiProvider>
                </ApiProvider>
              </Provider>
            </UserProvider>
          </Auth0ProviderWithRedirectCallback>
        );
      };

      return modularCreateBrowserRouter(Auth0ComponentProvider, App);
    }
    case AuthHookEnum.Authentik: {
      // TODO: possibly make them into environment variables
      // An idea can be to have the names of all these providers, applications etc
      // as a json-string (key-value pairs), stored in an environment variable that can be read by both
      // the front and back end services.
      // For now we can leave it hardcoded as this does not pose an immediate issue,
      // but should pick it up so we can approach the on-premise deployment in a modular way
      /**
       *`.authority` field : should be `localhost` instead of `docker-service-name`
       *  because communications happens through the browser, not the local network of container services.
       */
      const companySlug = slugify(import.meta.env.VITE_COMPANY_NAME!).replace('_', '-'); // conform with backend slug function
      const authDomain = import.meta.env.VITE_AUTHENTIK_DOMAIN;
      const authority = `${authDomain}/application/o/application-m2m-${companySlug}`;
      const clientid = `provider-client-id-m2m-${companySlug}`;

      const AuthentikComponentProvider = ({ children, ...props }: PropsWithChildren) => {
        const authentikConfig: AuthProviderProps = {
          authority: authority,
          client_id: clientid,
          redirect_uri: window.location.origin,
          post_logout_redirect_uri: window.location.origin,
          scope: 'openid profile email',
          onSigninCallback: () => {
            // 1. Check if we saved a deep-link path before logging in
            // Deep-link is created onBeforeSignin, so we remember this value.
            // else, we lose the data and we keep getting sent back to the homepage
            const returnTo = localStorage.getItem('user_authentik_return_to');

            if (returnTo) {
              localStorage.removeItem('user_authentik_return_to');
              // 2. Safely send the user back to their original destination
              window.location.replace(returnTo);
            } else {
              window.history.replaceState({}, document.title, window.location.origin);
            }
          },
          onSignoutCallback: () => {
            // This clears the code/state from the URL after login
            window.history.replaceState({}, document.title, window.location.origin);
          },
        };

        return (
          <AuthProvider {...authentikConfig}>
            <UserProvider>
              <Provider store={store}>
                <ApiProvider>
                  <M2MApiProvider>{children}</M2MApiProvider>
                </ApiProvider>
              </Provider>
            </UserProvider>
          </AuthProvider>
        );
      };

      return modularCreateBrowserRouter(AuthentikComponentProvider, AppOnPremise);
    }
    default: {
      throw new Error('No Identity Provider selected.');
    }
  }
};

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(<RouterProvider router={router()} />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
